/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import type { Post } from '@airasia-rn/social';
import type { RecommendationApiItem } from '../../models/recommendTabResponse';

const MOVEMENTS_TAB_TITLE = 'MoveMents';
const MOVEMENTS_LINE_OF_BUSINESS = 'SocialUGC';
import { HOME_PAGE_CATEGORY } from '../homepage';
import {
  RECOMMENDATION_LIST_NAME,
  RECOMMENDATION_SCREEN_NAME,
  QUARTILE_PAGE_SIZE,
  type RecommendationTrackingItem,
  RecommendationEvents,
  PROMOTION_LIST_NAME,
  RECOMMENDATION_BASE_CURRENCY,
} from './recommendation.meta';
import { RECOMMENDATION_TAB_TITLE_BY_ID } from './recommendation.tabs';

const SCREEN_PARAMS = {
  screenName: RECOMMENDATION_SCREEN_NAME,
  screen_name: RECOMMENDATION_SCREEN_NAME,
};

const normalizeTabName = (tabName: string) => tabName.toLowerCase();

export const trackRecommendationTabChange = (
  tabTitle: string,
  tabId?: string,
) => ({
  ...SCREEN_PARAMS,
  action: RecommendationEvents.tap,
  category: HOME_PAGE_CATEGORY.recommendation_widget,
  label: tabTitle,
  ...(tabId?.toLowerCase() === 'movements'
    ? { lineofbusiness: MOVEMENTS_LINE_OF_BUSINESS }
    : {}),
});

export const trackPromotionWidgetViewed = (
  tabName: string,
  quartile: number,
  maxIndex: number,
) => ({
  ...SCREEN_PARAMS,
  action: RecommendationEvents.Home_Promotion_Widget_Viewed,
  category: HOME_PAGE_CATEGORY.promotion_widget,
  label: `${normalizeTabName(tabName)}|${quartile}|${maxIndex}`,
});

export const trackRecommendationCardClick = (
  item: RecommendationApiItem,
  tabName: string,
  position: number,
) => ({
  ...SCREEN_PARAMS,
  action: RecommendationEvents.aa_home_recowidget_clicked,
  category: `${RECOMMENDATION_LIST_NAME}|${normalizeTabName(tabName)}`,
  currency: item.currency,
  lineofbusiness: item.lob,
  recommendationtype:
    item.recommendationType ?? item.details?.recommendationtype,
  productid: item.lobItemId,
  productname: item.title,
  productcategory: item.tags?.join(' '),
  price: item.price,
  strikethroughprice: item.originalPrice,
  position,
});

export const trackRecommendationScrollQuartile = (
  tabName: string,
  maxIndex: number,
) => {
  const percentage = (maxIndex * 100) / QUARTILE_PAGE_SIZE;
  return {
    ...SCREEN_PARAMS,
    action: RecommendationEvents.Home_RecoWidget_Viewed,
    category: HOME_PAGE_CATEGORY.recommendation_widget,
    label: `${normalizeTabName(tabName)}|${percentage}|${maxIndex}`,
  };
};

export const trackRecommendationViewBatch = (
  items: RecommendationApiItem[],
  tabName: string,
  startIndex: number,
) => ({
  ...SCREEN_PARAMS,
  action: RecommendationEvents.View_Item_List,
  category: `${RECOMMENDATION_LIST_NAME}|${RECOMMENDATION_TAB_TITLE_BY_ID.for_you}`,
  currency: items[0]?.currency ?? RECOMMENDATION_BASE_CURRENCY,
  items: items.map((item, idx) => {
    const price = item.unformatted?.price;
    const hasPrice = typeof price === 'number' && Number.isFinite(price);
    return {
      item_name: item.title,
      item_id: item.lobItemId,
      item_variant: item.lob,
      item_category: item.tags?.join(' '),
      item_category2:
        item.recommendationType ?? item.details?.recommendationtype,
      item_category3: item.originalPrice,
      ...(hasPrice ? { price } : {}),
      item_list_name: RECOMMENDATION_LIST_NAME,
      currency: item.currency,
      index: String(startIndex + idx + 1),
    };
  }),
});

export const trackMovementsCategoryClick = (
  title: string,
  tag: string,
  index: number,
) => ({
  ...SCREEN_PARAMS,
  action: RecommendationEvents.tap,
  category: `${MOVEMENTS_TAB_TITLE}|tap_category`,
  label: `${title}|${tag}|${index}`,
});

export const trackMovementsViewBatch = (
  posts: Post[],
  startIndex: number,
  currency: string = RECOMMENDATION_BASE_CURRENCY,
) => ({
  ...SCREEN_PARAMS,
  action: RecommendationEvents.View_Item_List,
  category: `${RECOMMENDATION_LIST_NAME}|${MOVEMENTS_TAB_TITLE}`,
  currency: currency.toUpperCase(),
  lineofbusiness: MOVEMENTS_LINE_OF_BUSINESS,
  items: posts.map((post, idx) => ({
    item_id: post.post_id,
    item_name: post.snippet || '',
    item_category: post.media?.type || '',
    item_list_name: MOVEMENTS_TAB_TITLE,
    index: startIndex + idx + 1,
  })),
});

export const trackPromotionListingViewBatch = (
  items: RecommendationApiItem[],
  startIndex: number,
) => ({
  ...SCREEN_PARAMS,
  action: RecommendationEvents.View_Item_List,
  category: PROMOTION_LIST_NAME,
  currency: items[0]?.currency ?? RECOMMENDATION_BASE_CURRENCY,
  items: items.map((item, idx) => ({
    item_id: item.lobItemId,
    item_name: item.title ?? null,
    item_variant: item.lob,
    item_category: item.subTitle ?? null,
    item_category2: item.tags?.length ? item.tags.join(' ') : null,
    index: String(startIndex + idx + 1),
    currency: item.currency,
    item_list_name: PROMOTION_LIST_NAME,
  })),
});

export const trackPromotionListingClick = (
  item: RecommendationApiItem,
  index: number,
) => ({
  ...SCREEN_PARAMS,
  action: RecommendationEvents.aa_home_promowidget_clicked,
  category: PROMOTION_LIST_NAME,
  currency: item.currency ?? RECOMMENDATION_BASE_CURRENCY,
  lineofbusiness: item.lob,
  productid: item.lobItemId,
  productname: item.title,
  productcategory: item.tags?.join(' '),
  price: item.price,
  strikethroughprice: item.originalPrice,
  position: index,
});

export const trackRecommendationApiFailure = (
  tabTitle: string,
  errorCode: string | number | undefined,
  errorMessage: string,
) => {
  const code = errorCode ?? 'UNKNOWN';
  const message = errorMessage || 'unknown_error';
  return {
    ...SCREEN_PARAMS,
    description: message,
    functionality: `Recommendation|${tabTitle}`,
    label: `${code}|${message}|recommendation_empty_result_title|Uh oh... our inspiration machine needs a break. Hang tight, and give it another try soon`,
    code,
    category: 'Homepage|Recommendation',
  };
};

export const trackRecommendationImpression = (
  item: RecommendationTrackingItem,
) => ({
  action: 'view',
  cardId: item.lobItemId ?? item.itemId ?? '',
  campaignId: item.itemId ?? item.lobItemId ?? '',
  category: item.lob ?? '',
  cardType: item.lob ?? '',
  destination: item.deepLink ?? '',
  title: item.title ?? '',
  tags: item.tags ?? [],
});

export const trackRecommendationClick = (item: RecommendationTrackingItem) => ({
  action: RecommendationEvents.tap,
  cardId: item.lobItemId ?? item.itemId ?? '',
  campaignId: item.itemId ?? item.lobItemId ?? '',
  category: item.lob ?? '',
  cardType: item.lob ?? '',
  destination: item.deepLink ?? '',
  title: item.title ?? '',
  tags: item.tags ?? [],
});
